// Pure query construction and row interpretation for the client-type restriction. Deliberately
// free of side-effectful imports so it can be unit-tested on its own - the same split
// clientListUrl.ts uses against SharePointRestServices.ts.
//
// The client list's site URL column is a Hyperlink: it cannot be indexed and cannot be relied
// on under $filter, and production lists run well past the 5000-item list view threshold. The
// SiteId column - single-line text holding the SharePoint site id, indexed by convention - is
// therefore the ONLY key this module ever queries.

// The Clients list schema is customer-controlled: resolve columns by candidate keys against
// display AND internal names, the same convention getInactiveClientSites uses.
export const CLIENT_TYPE_KEYS: string[] = ["klanttype", "klant type", "clienttype", "client type"];
export const CLIENT_SITE_ID_KEYS: string[] = ["siteid", "site id", "clientsiteid"];
// The client-group column: a multi-select lookup back into the Clients list (display name
// "Client Group", internal name "Klantgroep" on accountancy tenants). Resolved alongside the
// other two so the one fields request per list serves every client-list feature.
export const CLIENT_GROUP_KEYS: string[] = ["klantgroep", "klant groep", "clientgroup", "client group"];
// The site url column (a Hyperlink): same keys getInactiveClientSites uses. Never filtered on
// (see above) - only projected, so a member row can be matched to a loaded site by url.
export const CLIENT_SITE_URL_KEYS: string[] = ["siteurl", "site url", "url"];
// The client number column (display name "Client number", internal name "Client_x0020_number"
// on accountancy tenants): only projected, on a group's row, to label the group the way its
// site is named. Internal names arrive with SharePoint's encoded space, so that form is a key.
export const CLIENT_NUMBER_KEYS: string[] = ["client number", "client_x0020_number", "clientnumber", "klantnummer", "klant nummer"];
// The inactive flag ("Inactief", indexed): read by the inactive-client indicator at session start.
export const CLIENT_INACTIVE_KEYS: string[] = ["inactief", "inactive"];

// Every request to the client list carries this. Reads are shared while in flight, so one that
// never answered would hold every later lookup of that site or group for the session; axios has
// no timeout of its own. retry-axios retries a timed-out request twice, so the wait is bounded.
export const CLIENT_LIST_REQUEST_TIMEOUT_MS = 15000;

export interface IClientListField {
  InternalName: string;
  Title: string;
  /** SharePoint's field type ("LookupMulti", "Choice", ...); present on every fields read. */
  TypeAsString?: string;
}

// The group column is only usable as a lookup: its `<Internal>Id` projection exists for no other
// type, and a same-named Choice or Text column would 400 the session copy's read every
// client-group feature starts from. An untyped field (no TypeAsString) is trusted, so a field
// list without types keeps working.
const isLookupField = (field: IClientListField | undefined): boolean =>
  !field?.TypeAsString || /^Lookup(Multi)?$/i.test(field.TypeAsString.trim());

export interface IClientTypeColumns {
  siteIdInternal?: string;
  typeInternal?: string;
  groupInternal?: string;
  /** The group column is a single-value lookup: its id projection is one id, or null for a row
   * in no group, instead of a list. Unset for the multi-value lookup the tenants use. */
  groupIsSingleValue?: boolean;
  siteUrlInternal?: string;
  numberInternal?: string;
  inactiveInternal?: string;
}

/** The three states the restriction distinguishes. `none` is NOT `unknown`: a site with no
 * client row genuinely has no type and must fail an allow-list, whereas `unknown` (unreadable,
 * unconfigured, malformed) makes the whole dimension inert. */
export type ClientTypeLookup =
  | { state: "unknown" }
  | { state: "none" }
  | { state: "known"; clientType: string };

const resolveInternalName = (fields: IClientListField[] | undefined, keys: string[]): string | undefined =>
  Array.isArray(fields)
    ? fields.find(field => field
      && keys.some(key => field.Title?.toLowerCase()?.trim() === key
        || field.InternalName?.toLowerCase()?.trim() === key))?.InternalName
    : undefined;

export const resolveClientTypeColumns = (fields?: IClientListField[]): IClientTypeColumns => {
  const groupInternal: string | undefined = resolveInternalName(Array.isArray(fields) ? fields.filter(isLookupField) : fields, CLIENT_GROUP_KEYS);
  const groupField: IClientListField | undefined = groupInternal && Array.isArray(fields) ? fields.find(field => field?.InternalName === groupInternal) : undefined;
  const columns: IClientTypeColumns = {
    siteIdInternal: resolveInternalName(fields, CLIENT_SITE_ID_KEYS),
    typeInternal: resolveInternalName(fields, CLIENT_TYPE_KEYS),
    groupInternal,
    siteUrlInternal: resolveInternalName(fields, CLIENT_SITE_URL_KEYS),
    numberInternal: resolveInternalName(fields, CLIENT_NUMBER_KEYS),
    inactiveInternal: resolveInternalName(fields, CLIENT_INACTIVE_KEYS),
  };
  if (/^Lookup$/i.test(groupField?.TypeAsString?.trim() ?? "")) {
    columns.groupIsSingleValue = true;
  }
  return columns;
};

// Lowercase-only by contract: extractSharePointSiteId lowercases before returning.
const LOWER_GUID_PATTERN: RegExp = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

/**
 * A SINGLE equality predicate on the indexed SiteId column - never an `or` (SharePoint may
 * decline to serve one from an index on a large list) and never the url column. $top=2 rather
 * than 1 so that a site listed twice with conflicting types is detectable instead of silently
 * resolving to whichever row came back first. Detection is deliberately partial: a conflict
 * appearing only from a THIRD duplicate row onward goes unseen - any duplicate is already bad
 * data, and two rows is the cheapest window that catches the common case without paging.
 *
 * The guid is interpolated into $filter unescaped, so it is re-checked here rather than only
 * trusting the caller contract (extractSharePointSiteId output): defense in depth against a
 * future caller passing a raw string into an OData predicate.
 */
export const buildClientTypeItemsUrl = (
  webUrl: string,
  listEndpoint: string,
  columns: IClientTypeColumns,
  sharePointSiteId: string,
): string | undefined =>
  columns?.typeInternal
    ? buildClientRowBySiteIdUrl(webUrl, listEndpoint, columns.siteIdInternal, [columns.typeInternal], sharePointSiteId)
    : undefined;

/** The one indexed point query every client-list feature shares: the row(s) for a site
 * collection, projected to `selectFields`. Same guarantees as above - a single equality on
 * the SiteId column, guid re-checked before interpolation, $top=2 for conflict detection. */
export const buildClientRowBySiteIdUrl = (
  webUrl: string,
  listEndpoint: string,
  siteIdInternal: string | undefined,
  selectFields: string[],
  sharePointSiteId: string,
): string | undefined => {
  if (!webUrl || !listEndpoint || !siteIdInternal || !Array.isArray(selectFields) || selectFields.length === 0
    || !sharePointSiteId || !LOWER_GUID_PATTERN.test(sharePointSiteId)) {
    return undefined;
  }

  return `${webUrl}/_api/web/${listEndpoint}/items`
    + `?$select=${selectFields.join(",")}`
    + `&$top=2`
    + `&$filter=${siteIdInternal} eq '${sharePointSiteId}'`;
};

/** A scalar (or OData-wrapped scalar) type value, "" for a genuinely empty cell, or undefined
 * for a shape this module does not understand. `undefined` is load-bearing: an uninterpretable
 * value must surface as `unknown` (inert), never collapse to "" and masquerade as `none`
 * (which fails allow-lists) - the schema is customer-controlled, so unexpected shapes are a
 * WHEN, not an IF. */
const readScalarTypeValue = (value: any): string | undefined => {
  if (value === null || value === undefined) {
    return "";
  }
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return String(value).trim();
  }
  return undefined;
};

const readTypeValue = (row: any, typeInternal: string): string | undefined => {
  const value: any = row?.[typeInternal];
  // Before the object branch: typeof null === "object".
  if (value === null || value === undefined) {
    return "";
  }
  // A multi-choice column returns an array (bare or {results:[...]} depending on OData mode).
  // One distinct non-blank element is as good as a scalar; several distinct elements are
  // ambiguous against a single-type model and must go inert rather than pick a winner.
  const elements: any[] | undefined = Array.isArray(value) ? value
    : Array.isArray(value?.results) ? value.results
      : undefined;
  if (elements) {
    const read: (string | undefined)[] = elements.map(readScalarTypeValue);
    if (read.some(element => element === undefined)) {
      return undefined;
    }
    const nonBlank: string[] = (read as string[]).filter(element => element.length > 0);
    if (new Set(nonBlank.map(element => element.toUpperCase())).size > 1) {
      return undefined;
    }
    return nonBlank.length === 0 ? "" : nonBlank[0];
  }
  // Text and Choice come back as a string; a wrapped shape exposes Value. An object WITHOUT
  // a Value key is a shape this module does not understand (Managed Metadata {Label,...},
  // Lookup {Id,Title}, verbose-OData non-array results) - it carries data, so it must go
  // `unknown`/inert, never collapse to "" and masquerade as a genuinely empty cell (`none`
  // fails allow-lists).
  if (typeof value === "object") {
    return "Value" in value ? readScalarTypeValue(value.Value) : undefined;
  }
  return readScalarTypeValue(value);
};

export const interpretClientTypeRows = (rows: any[], typeInternal: string): ClientTypeLookup => {
  // `none` fails an allow-list, so it is reserved for a WELL-FORMED empty answer. A payload
  // that is not the expected row array (proxy rewrite, unexpected OData shape) is a failure
  // and resolves to `unknown` - never fail closed on a malformed response.
  if (!Array.isArray(rows)) {
    return { state: "unknown" };
  }
  if (rows.length === 0) {
    return { state: "none" };
  }

  const readValues: (string | undefined)[] = rows.map(row => readTypeValue(row, typeInternal));
  if (readValues.some(value => value === undefined)) {
    return { state: "unknown" };
  }

  const values: string[] = (readValues as string[]).filter(value => value.length > 0);

  if (values.length === 0) {
    return { state: "none" };
  }

  // Conflicting duplicates are bad data. Picking a winner would silently decide which
  // datasources a user sees, so the dimension goes inert instead.
  if (new Set(values.map(value => value.toUpperCase())).size > 1) {
    return { state: "unknown" };
  }

  return { state: "known", clientType: values[0] };
};
